// src/pages/ApiReference.js
import React, { useState, useEffect, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { getBundledModelCatalog } from '../constants/modelCatalog';
import ThemeToggle from './components/ThemeToggle';

const scrollToSection = (sectionId) => {
  const section = document.getElementById(sectionId);
  if (section) {
    section.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }
};

const SECTIONS = [
  { id: 'introduction', label: 'Introduction' },
  { id: 'authentication', label: 'Authentication' },
  { id: 'endpoints', label: 'Endpoints' },
  { id: 'parameters', label: 'Request Parameters' },
  { id: 'streaming', label: 'Streaming & Responses' },
  { id: 'idempotency', label: 'Idempotency' },
  { id: 'billing', label: 'Billing & Budgets' },
  { id: 'models', label: 'Supported Models' },
  { id: 'error-handling', label: 'Error Handling' },
];

const SECTION_LABELS = Object.fromEntries(SECTIONS.map((s) => [s.id, s.label]));

const PROVIDER_STYLES = {
  openai: 'bg-emerald-500',
  anthropic: 'bg-orange-500',
  gemini: 'bg-blue-500',
  xai: 'bg-slate-800 dark:bg-slate-600',
  deepseek: 'bg-indigo-500',
  kimi: 'bg-purple-500',
  glm: 'bg-teal-500',
};

const CURL_STREAM = `curl -N -X POST https://qualitycompute.henosis.us/v1/responses/stream \\
  -H "Content-Type: application/json" \\
  -H "Authorization: Bearer QC_YOUR_API_KEY" \\
  -H "Idempotency-Key: demo-poem-001" \\
  -d '{
    "model": "o4-mini-B10",
    "input": "Generate a poem about space.",
    "stream": true
  }'`;

const SSE_SAMPLE = `event: response.started
data: {"type":"response.started","response_id":"resp_1a2b...","strategy":"best_of_n","model":"o4-mini"}

event: response.progress
data: {"type":"response.progress","response_id":"resp_1a2b...","stage":"draft_call_completed","completed":1,"total":10}

event: response.output_text.delta
data: {"type":"response.output_text.delta","response_id":"resp_1a2b...","delta":"Across the velvet dark, "}

...

event: response.completed
data: {"type":"response.completed","response_id":"resp_1a2b...","strategy":"best_of_n","model":"o4-mini","output_text":"Across the velvet dark, ...","usage":{"input_tokens":52,"output_tokens":214,"reasoning_tokens":38,"total_tokens":304},"bill":{"provider_cost_usd":0.0142,"markup_usd":0.0,"total_cost_usd":0.0142},...}`;

const CURL_BUFFERED = `curl -X POST https://qualitycompute.henosis.us/v1/responses \\
  -H "Content-Type: application/json" \\
  -H "Authorization: Bearer QC_YOUR_API_KEY" \\
  -H "Idempotency-Key: demo-buffered-001" \\
  -d '{
    "model": "gpt-5.4",
    "input": [{"role": "user", "content": "Summarize SSE in one sentence."}],
    "stream": true,
    "strategy": "best_of_n",
    "candidates": 4,
    "mode": "synthesis",
    "max_output_tokens": 400
  }'`;

const COMPLETED_EVENT_SAMPLE = `{
  "type": "response.completed",
  "response_id": "resp_1a2b...",
  "strategy": "best_of_n",
  "model": "o4-mini",
  "output_text": "The final selected or synthesized text.",
  "usage": {
    "input_tokens": 52,
    "output_tokens": 214,
    "reasoning_tokens": 38,
    "total_tokens": 304
  },
  "bill": {
    "provider_cost_usd": 0.0142,
    "markup_usd": 0.0,
    "total_cost_usd": 0.0142
  },
  "tool_calls": [],
  "web_search": [],
  "service_tier": null,
  "refusal": null,
  "stop_details": null,
  "awaiting_tool_results": false
}`;

const WS_SAMPLE = `// First message after connecting to wss://qualitycompute.henosis.us/v1/ws/responses
{
  "authorization": "Bearer QC_YOUR_API_KEY",
  "idempotency_key": "demo-ws-001",
  "request": {
    "model": "o4-mini",
    "input": "Hello over WebSocket.",
    "stream": true
  }
}`;

const MODELS_RESPONSE_SAMPLE = `{
  "models": [
    {
      "id": "claude-opus-5-5",
      "provider": "anthropic",
      "input": 4.00,
      "output": 20.00,
      "max_tokens": 128000,
      "context_window": 1000000,
      "thinking_default": "adaptive",
      "thinking_display_default": "updates",
      "default_thinking_effort": "medium",
      "default_speed_mode": "standard",
      "capabilities": {
        "thinking": true,
        "best_of_n": true,
        "selection": true,
        "synthesis": true,
        "native_web_search": true,
        "streaming": true
      }
    }
  ],
  "registered_providers": ["openai", "anthropic", "gemini", "xai", "deepseek", "kimi", "glm"]
}`;

const ERROR_DETAIL_SAMPLE = `// Standard API errors (400, 401, 402, 404, 409)
{ "detail": "QC is streaming-only; set stream=true" }

// Validation errors (422): detail is a list of field errors
{ "error": "RequestValidationError", "detail": [ { "loc": ["body", "input"], "msg": "...", "type": "..." } ], "path": "..." }

// Unhandled / framework / database errors (500, 503)
{ "error": "UnhandledException", "detail": "...", "path": "https://...", "type": "RuntimeError" }`;

const STREAM_ERROR_SAMPLE = `event: response.error
data: {"type":"response.error","error":"provider connection reset"}`;

const GENERATION_ENDPOINTS = [
  { method: 'POST', path: '/v1/responses/stream', desc: 'Generate a response as a Server-Sent Events stream. The canonical streaming endpoint.' },
  { method: 'POST', path: '/v1/responses', desc: 'Buffered variant: the server consumes the stream and returns the final response.completed summary as a single JSON object.' },
  { method: 'POST', path: '/v1/responses/background', desc: 'Submit a generation as a background job. Send "Prefer: respond-async" (or "return_immediately": true in the body) to get the job record back immediately; otherwise the call waits and returns the completed record.' },
  { method: 'GET', path: '/v1/responses/background/{id}', desc: 'Fetch a background job record: { id, status (running | completed | failed | cancelled), model, strategy, response, error }.' },
  { method: 'DELETE', path: '/v1/responses/background/{id}', desc: 'Cancel a background job you own.' },
  { method: 'WS', path: '/v1/ws/responses', desc: 'WebSocket variant. Send one JSON frame with authorization, idempotency_key, and request; events arrive as JSON messages. Budget denials close with code 1008, errors with 1011.' },
];

const STRATEGY_ENDPOINTS = [
  { method: 'POST', path: '/v1/strategies/best-of-n/stream', desc: 'Force strategy=best_of_n (candidates, mode, judge_model, synthesis_model apply).' },
  { method: 'POST', path: '/v1/strategies/judge-selection/stream', desc: 'Force strategy=judge_selection (judge_model is required).' },
  { method: 'POST', path: '/v1/strategies/synthesis/stream', desc: 'Force strategy=synthesis (optional synthesis_model).' },
  { method: 'POST', path: '/v1/strategies/collaborative/stream', desc: 'Force strategy=collaborative (team_leader_model, student_model, judge_model required).' },
];

const ESTIMATION_ENDPOINTS = [
  { method: 'POST', path: '/v1/cost-estimates', desc: 'Dry-run cost check: returns estimated_usage, estimated_bill, allowed, and denial_reason without generating or charging anything.' },
  { method: 'POST', path: '/v1/token-estimates', desc: 'Token and bill estimate for a request payload (same body as a generation call).' },
  { method: 'POST', path: '/v1/cost-plans', desc: 'Detailed per-call cost plan: every internal model call (drafts, judge, synthesis) with per-call usage and assumptions.' },
];

const ACCOUNT_ENDPOINTS = [
  { method: 'GET', path: '/v1/usage', desc: 'Your usage ledger, newest first. Query param format=json (default) or format=csv (downloads qualitycompute_usage.csv). Rows include usage_id, strategy, model, provider, input/output/reasoning/total tokens, provider_cost_usd, total_cost_usd, created_at.' },
  { method: 'GET', path: '/v1/credits', desc: 'Your balance and payment history: { "remaining_credits_usd": float, "ledger": [{ payment_intent_id, credits_added, timestamp, ... }] }.' },
  { method: 'GET', path: '/v1/me', desc: 'Identity and limits for the calling key: user_id, email, api_key { label, scopes, created_at }, remaining_credits_usd, user_budget_cap_usd, api_key_budget_cap_usd. Never echoes the raw key.' },
];

const RUNS_ENDPOINTS = [
  { method: 'POST', path: '/v1/runs', desc: 'Create a durable run (queued server-side; optional webhook_url in the body for completion callbacks).' },
  { method: 'GET', path: '/v1/runs/{id}', desc: 'Fetch run status and result.' },
  { method: 'GET', path: '/v1/runs/{id}/snapshot', desc: 'Full run snapshot including recorded events.' },
  { method: 'POST', path: '/v1/runs/{id}/cancel', desc: 'Cancel a run you own.' },
  { method: 'GET', path: '/v1/runs/{id}/stream', desc: 'Replay run events as SSE; resume with ?after_seq=N.' },
];

const METADATA_ENDPOINTS = [
  { method: 'GET', path: '/v1/models', desc: 'The live model catalog with per-million-token pricing (no auth required). Legacy alias: /api/models.' },
  { method: 'GET', path: '/v1/model-capabilities', desc: 'Per-model capability flags, max_tokens, and supported service_tiers. Legacy alias: /api/model_capabilities.' },
  { method: 'GET', path: '/v1/providers/status', desc: 'Provider health: configured / degraded / routing_eligible per provider, plus the retry policy.' },
  { method: 'GET', path: '/v1/providers/failover-policy', desc: 'The current provider failover policy.' },
  { method: 'GET', path: '/health', desc: 'Service health and supported feature flags.' },
];

const LEGACY_ENDPOINTS = [
  { method: 'POST', path: '/api/generate', desc: 'Legacy alias of /v1/responses/stream (same body, headers, and SSE output).' },
  { method: 'POST', path: '/api/generate_collaborative', desc: 'Legacy alias of /v1/strategies/collaborative/stream.' },
  { method: 'POST', path: '/api/chat', desc: 'Legacy buffered alias (stream: true is injected server-side; returns the completed summary as JSON).' },
  { method: 'POST', path: '/api/chat/stream', desc: 'Legacy streaming alias of /v1/responses/stream.' },
  { method: 'GET', path: '/api/openai/responses/{id}', desc: 'Retrieve a provider-side OpenAI background response by id (helper for background: true on OpenAI models).' },
  { method: 'POST', path: '/api/openai/responses/{id}/cancel', desc: 'Cancel a provider-side OpenAI background response.' },
  { method: 'POST', path: '/v1/tools/callback', desc: 'Acknowledge an externally executed tool call (alias: /api/tools/callback). Pairs with user_tools / tool_results.' },
];

const CORE_PARAMS = [
  { name: 'model', type: 'string', required: 'Yes', desc: 'Canonical model id or alias (e.g. "o4-mini", "gpt-5.4"). An optional trailing -B[N] suffix opts into best-of-N — see the note below this table.' },
  { name: 'input', type: 'string or array', required: 'Yes', desc: 'The prompt text, or an array of message objects like [{"role": "user", "content": "Hello"}]. Allowed roles: system, user, assistant, tool.' },
  { name: 'stream', type: 'boolean', required: 'Yes', desc: 'Must be true on every generation endpoint — the API is streaming-first. Buffered endpoints still require it; the server consumes the stream and returns the final summary.' },
  { name: 'strategy', type: 'string', required: 'No', desc: 'single (default), best_of_n, judge_selection, synthesis, or collaborative. Explicit strategy in the body always wins over a -B model suffix.' },
  { name: 'max_output_tokens', type: 'integer', required: 'No', desc: 'Maximum output tokens per model call (minimum 1). There is no max_tokens parameter.' },
  { name: 'reasoning_effort', type: 'string', required: 'No', desc: 'none, low, medium, high, or xhigh — for reasoning-capable models.' },
  { name: 'thinking_budget_tokens', type: 'integer', required: 'No', desc: 'Explicit thinking-token budget (minimum 1) for models with budgeted thinking.' },
  { name: 'gemini_thinking_level', type: 'string', required: 'No', desc: 'minimal, low, medium, or high — Gemini-specific thinking control.' },
  { name: 'anthropic_effort', type: 'string', required: 'No', desc: 'Anthropic effort control. claude-opus-5-5 defaults to medium when omitted and preserves explicit low, medium, high, max, or xhigh.' },
  { name: 'anthropic_thinking_display', type: 'string', required: 'No', desc: 'updates (default for claude-opus-5-5) forwards safe progress text; summarized is opt-in, and off suppresses public thinking progress.' },
  { name: 'anthropic_thinking_block_binding_prefix_mismatch_behavior', type: 'string', required: 'No', desc: 'Set to drop_block when an edited prefix may invalidate a native thinking block; claude-opus-5-5 uses this by default. This uses the documented thinking-binding-controls-2026-08-01 beta; QC does not validate signatures locally.' },
  { name: 'service_tier', type: 'string', required: 'No', desc: 'auto, default, standard, flex, or priority. Supported tiers vary by model — check /v1/model-capabilities.' },
  { name: 'background', type: 'boolean', required: 'No', desc: 'Request provider-side background processing where supported. Set automatically by /v1/responses/background.' },
  { name: 'request_budget_usd', type: 'float', required: 'No', desc: 'Hard cost cap for this single request. If the pre-flight estimate exceeds it, the call is rejected with 402.' },
  { name: 'api_key_budget_usd / user_budget_usd', type: 'float', required: 'No', desc: 'Additional per-request caps checked against the estimate, alongside any persistent key/user caps configured in the dashboard.' },
  { name: 'prompt_cache_key', type: 'string', required: 'No', desc: 'Provider prompt-cache routing key for cache-friendly workloads.' },
  { name: 'metadata', type: 'object', required: 'No', desc: 'Free-form object carried through the request.' },
];

const STRATEGY_PARAMS = [
  { name: 'candidates', type: 'integer', required: 'No', desc: 'Number of parallel drafts for best_of_n, judge_selection, and synthesis. Range 2–32, default 2.' },
  { name: 'mode', type: 'string', required: 'No', desc: 'best_of_n only: "selection" (default — a judge picks the best draft) or "synthesis" (a model merges the drafts).' },
  { name: 'judge_model', type: 'string', required: 'Varies', desc: 'Optional on best_of_n (defaults to model); required for judge_selection and collaborative. Also accepts a -B suffix, which is stripped.' },
  { name: 'synthesis_model', type: 'string', required: 'No', desc: 'Model used to synthesize drafts in best_of_n (mode: synthesis) and synthesis strategies; defaults to model.' },
  { name: 'team_leader_model', type: 'string', required: 'collaborative', desc: 'Model that leads and refines the collaborative session.' },
  { name: 'student_model', type: 'string', required: 'collaborative', desc: 'Model used for the student cohort.' },
  { name: 'num_students', type: 'integer', required: 'No', desc: 'collaborative: number of students. Range 2–32, default 3.' },
  { name: 'num_turns', type: 'integer', required: 'No', desc: 'collaborative: number of refinement turns. Range 1–12, default 2.' },
];

const EVENT_TYPES = [
  { name: 'response.started', desc: 'First event: carries response_id, strategy, and model.' },
  { name: 'response.progress', desc: 'Strategy milestones — e.g. draft_calls_started, draft_call_completed (with completed/total), judge_completed, student_turn_completed.' },
  { name: 'response.output_text.delta', desc: 'Incremental output text in the delta field.' },
  { name: 'response.thinking.delta', desc: 'Public progress-only thinking text when Anthropic display=updates. It never contains signatures or native replay blocks.' },
  { name: 'response.tool_call', desc: 'The model requested a tool call; payload contains the tool_call object (name, arguments, tool_call_id).' },
  { name: 'response.error', desc: 'In-band failure. Delivered inside the stream under HTTP 200 — the HTTP status never changes mid-stream.' },
  { name: 'response.completed', desc: 'Terminal event carrying the full execution summary (see below). Exactly one per successful stream.' },
];

const STATUS_CODES = [
  { code: '200', status: 'OK', tone: 'text-emerald-600 dark:text-emerald-400', desc: 'Request accepted. Note: streaming failures after the response starts arrive as in-band response.error events under HTTP 200.' },
  { code: '400', status: 'Bad Request', tone: 'text-amber-600 dark:text-amber-400', desc: 'Missing "stream": true, unknown or disabled model, -B suffix out of range (must be 1–32), or missing Idempotency-Key header.' },
  { code: '401', status: 'Unauthorized', tone: 'text-rose-600 dark:text-rose-400', desc: 'Missing or invalid API key (or wrong auth scheme — only Authorization: Bearer is accepted).' },
  { code: '402', status: 'Payment Required', tone: 'text-rose-600 dark:text-rose-400', desc: 'Pre-flight estimate denied: insufficient credits, a budget cap exceeded, or a plan entitlement. The reason is in detail.' },
  { code: '404', status: 'Not Found', tone: 'text-rose-600 dark:text-rose-400', desc: 'Endpoint or resource not found (including background jobs and runs owned by another key).' },
  { code: '409', status: 'Conflict', tone: 'text-rose-600 dark:text-rose-400', desc: 'Idempotency-Key reused with a different payload, or a request with the same key is still in progress.' },
  { code: '422', status: 'Unprocessable Entity', tone: 'text-rose-600 dark:text-rose-400', desc: 'Body failed validation (detail is a list of field errors).' },
  { code: '500', status: 'Server Error', tone: 'text-rose-600 dark:text-rose-400', desc: 'Unhandled server error before streaming starts. Provider failures mid-stream surface as response.error events instead.' },
  { code: '503', status: 'Service Unavailable', tone: 'text-rose-600 dark:text-rose-400', desc: 'Database unavailable or a required provider client is not configured.' },
];

// --- Search index (static part) --------------------------------------------
const ENDPOINT_GROUPS = [
  ['Generation', GENERATION_ENDPOINTS],
  ['Strategy shortcuts', STRATEGY_ENDPOINTS],
  ['Estimation', ESTIMATION_ENDPOINTS],
  ['Account', ACCOUNT_ENDPOINTS],
  ['Runs', RUNS_ENDPOINTS],
  ['Metadata', METADATA_ENDPOINTS],
  ['Legacy', LEGACY_ENDPOINTS],
];

const STATIC_SEARCH_INDEX = [
  ...SECTIONS.map((s) => ({
    sectionId: s.id,
    kind: 'Section',
    label: s.label,
    snippet: 'Jump to this section',
    haystack: s.label.toLowerCase(),
  })),
  ...ENDPOINT_GROUPS.flatMap(([group, rows]) =>
    rows.map((row) => ({
      sectionId: 'endpoints',
      kind: row.method,
      label: row.path,
      snippet: `${group} — ${row.desc}`,
      haystack: `${row.method} ${row.path} ${group} ${row.desc}`.toLowerCase(),
    }))
  ),
  ...[...CORE_PARAMS, ...STRATEGY_PARAMS].map((p) => ({
    sectionId: 'parameters',
    kind: 'Param',
    label: p.name,
    snippet: p.desc,
    haystack: `${p.name} ${p.type} ${p.desc}`.toLowerCase(),
  })),
  ...EVENT_TYPES.map((e) => ({
    sectionId: 'streaming',
    kind: 'Event',
    label: e.name,
    snippet: e.desc,
    haystack: `${e.name} ${e.desc}`.toLowerCase(),
  })),
  ...STATUS_CODES.map((c) => ({
    sectionId: 'error-handling',
    kind: 'HTTP',
    label: `${c.code} ${c.status}`,
    snippet: c.desc,
    haystack: `${c.code} ${c.status} ${c.desc}`.toLowerCase(),
  })),
];

const SEARCH_KIND_STYLES = {
  Section: 'bg-slate-100 text-slate-600 dark:bg-slate-800 dark:text-slate-300',
  Param: 'bg-indigo-50 text-indigo-700 dark:bg-indigo-950/60 dark:text-indigo-300',
  Event: 'bg-purple-50 text-purple-700 dark:bg-purple-950/60 dark:text-purple-300',
  Model: 'bg-teal-50 text-teal-700 dark:bg-teal-950/60 dark:text-teal-300',
  HTTP: 'bg-amber-50 text-amber-700 dark:bg-amber-950/60 dark:text-amber-300',
  POST: 'bg-emerald-50 text-emerald-700 dark:bg-emerald-950/60 dark:text-emerald-300',
  GET: 'bg-sky-50 text-sky-700 dark:bg-sky-950/60 dark:text-sky-300',
  DELETE: 'bg-rose-50 text-rose-700 dark:bg-rose-950/60 dark:text-rose-300',
  WS: 'bg-violet-50 text-violet-700 dark:bg-violet-950/60 dark:text-violet-300',
};
// ---------------------------------------------------------------------------

const methodBadge = (method) => {
  const styles = {
    POST: 'bg-emerald-50 text-emerald-700 ring-emerald-600/20 dark:bg-emerald-950/50 dark:text-emerald-300 dark:ring-emerald-400/20',
    GET: 'bg-sky-50 text-sky-700 ring-sky-600/20 dark:bg-sky-950/50 dark:text-sky-300 dark:ring-sky-400/20',
    DELETE: 'bg-rose-50 text-rose-700 ring-rose-600/20 dark:bg-rose-950/50 dark:text-rose-300 dark:ring-rose-400/20',
    WS: 'bg-violet-50 text-violet-700 ring-violet-600/20 dark:bg-violet-950/50 dark:text-violet-300 dark:ring-violet-400/20',
  };
  return (
    <span className={`inline-flex px-2 py-0.5 rounded-md text-[11px] font-semibold tracking-wide ring-1 ring-inset whitespace-nowrap ${styles[method] || 'bg-slate-50 text-slate-700 ring-slate-600/20 dark:bg-slate-800 dark:text-slate-300 dark:ring-slate-400/20'}`}>
      {method}
    </span>
  );
};

const tableHeadCell = (label) => (
  <th scope="col" className="px-4 py-3 text-left text-[11px] font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wider">
    {label}
  </th>
);

const renderEndpointTable = (rows) => (
  <div className="rounded-xl border border-slate-200 dark:border-slate-800 overflow-hidden mb-6">
    <div className="overflow-x-auto">
      <table className="min-w-full divide-y divide-slate-200 dark:divide-slate-800">
        <thead className="bg-slate-50/80 dark:bg-slate-800/60">
          <tr>
            {tableHeadCell('Method')}
            {tableHeadCell('Path')}
            {tableHeadCell('Description')}
          </tr>
        </thead>
        <tbody className="bg-white dark:bg-slate-900 divide-y divide-slate-100 dark:divide-slate-800/70 text-sm">
          {rows.map((row) => (
            <tr key={`${row.method} ${row.path}`} className="hover:bg-slate-50/60 dark:hover:bg-slate-800/40 transition-colors">
              <td className="px-4 py-3">{methodBadge(row.method)}</td>
              <td className="px-4 py-3 font-mono text-[13px] text-indigo-950 dark:text-indigo-200 whitespace-nowrap">{row.path}</td>
              <td className="px-4 py-3 text-slate-600 dark:text-slate-400">{row.desc}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  </div>
);

const renderParamTable = (rows) => (
  <div className="rounded-xl border border-slate-200 dark:border-slate-800 overflow-hidden mb-4">
    <div className="overflow-x-auto">
      <table className="min-w-full divide-y divide-slate-200 dark:divide-slate-800">
        <thead className="bg-slate-50/80 dark:bg-slate-800/60">
          <tr>
            {tableHeadCell('Parameter')}
            {tableHeadCell('Type')}
            {tableHeadCell('Required')}
            {tableHeadCell('Description')}
          </tr>
        </thead>
        <tbody className="bg-white dark:bg-slate-900 divide-y divide-slate-100 dark:divide-slate-800/70 text-sm">
          {rows.map((row) => (
            <tr key={row.name} className="hover:bg-slate-50/60 dark:hover:bg-slate-800/40 transition-colors">
              <td className="px-4 py-3 font-mono text-[13px] text-indigo-700 dark:text-indigo-300 whitespace-nowrap">{row.name}</td>
              <td className="px-4 py-3 text-slate-500 dark:text-slate-400 whitespace-nowrap">{row.type}</td>
              <td className="px-4 py-3">
                <span className={`text-xs font-medium ${row.required === 'Yes' ? 'text-rose-600 dark:text-rose-400' : 'text-slate-500 dark:text-slate-400'}`}>
                  {row.required}
                </span>
              </td>
              <td className="px-4 py-3 text-slate-600 dark:text-slate-400">{row.desc}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  </div>
);

const infoBox = (children, key) => (
  <div key={key} className="bg-indigo-50/70 ring-1 ring-inset ring-indigo-100 dark:bg-indigo-950/30 dark:ring-indigo-900/70 rounded-xl p-4 flex items-start gap-3 mb-4">
    <div className="w-6 h-6 rounded-full bg-indigo-100 dark:bg-indigo-900/70 flex items-center justify-center flex-shrink-0 mt-0.5">
      <svg className="w-4 h-4 text-indigo-600 dark:text-indigo-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
      </svg>
    </div>
    <div className="text-sm text-slate-700 dark:text-slate-300 space-y-2">{children}</div>
  </div>
);

const codeBlock = (code) => (
  <pre className="bg-slate-900 p-4 sm:p-5 rounded-xl overflow-x-auto text-[13px] leading-relaxed font-mono text-slate-300 mb-4 border border-slate-800 dark:border-slate-700/70 shadow-sm">
    {code}
  </pre>
);

const inlineCode = (text) => (
  <code className="px-1.5 py-0.5 rounded-md bg-slate-100 border border-slate-200/80 dark:bg-slate-800 dark:border-slate-700 text-[0.85em] text-indigo-800 dark:text-indigo-300 font-mono">
    {text}
  </code>
);

function SectionCard({ id, title, children }) {
  return (
    <section id={id} className="bg-white dark:bg-slate-900 rounded-2xl border border-slate-200/80 dark:border-slate-800 shadow-sm overflow-hidden scroll-mt-28">
      <div className="p-6 sm:p-8">
        <div className="flex items-center gap-3 mb-6">
          <span className="w-1.5 h-6 rounded-full bg-gradient-to-b from-indigo-500 to-purple-500" aria-hidden="true" />
          <h2 className="text-2xl font-bold tracking-tight">{title}</h2>
        </div>
        {children}
      </div>
    </section>
  );
}

export default function ApiReference() {
  const [elevated, setElevated] = useState(false);
  const [activeSection, setActiveSection] = useState('introduction');
  const [copied, setCopied] = useState(false);
  const [modelCatalog] = useState(() => getBundledModelCatalog());
  const [query, setQuery] = useState('');

  useEffect(() => {
    const onScroll = () => setElevated(window.scrollY > 10);
    window.addEventListener('scroll', onScroll);
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  // Scrollspy: highlight the section currently in view.
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        const visible = entries
          .filter((entry) => entry.isIntersecting)
          .sort((a, b) => a.boundingClientRect.top - b.boundingClientRect.top);
        if (visible.length > 0) {
          setActiveSection(visible[0].target.id);
        }
      },
      { rootMargin: '-15% 0px -70% 0px' }
    );
    SECTIONS.forEach((section) => {
      const el = document.getElementById(section.id);
      if (el) observer.observe(el);
    });
    return () => observer.disconnect();
  }, []);

  const groupedModels = useMemo(() => {
    const groups = {};
    for (const model of modelCatalog) {
      const provider = model.provider || 'unknown';
      if (!groups[provider]) {
        groups[provider] = [];
      }
      groups[provider].push(model);
    }
    return groups;
  }, [modelCatalog]);

  // Full search index: static docs entries plus the live model catalog.
  const searchIndex = useMemo(
    () => [
      ...STATIC_SEARCH_INDEX,
      ...modelCatalog.map((model) => ({
        sectionId: 'models',
        kind: 'Model',
        label: model.id,
        snippet: `${model.provider || 'unknown'} — $${model.input}/M in, $${model.output}/M out`,
        haystack: `${model.id} ${model.provider || ''} model`.toLowerCase(),
      })),
    ],
    [modelCatalog]
  );

  const searchResults = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (q.length < 2) return [];
    const scored = [];
    for (const entry of searchIndex) {
      const label = entry.label.toLowerCase();
      let score = -1;
      if (label.startsWith(q)) score = 0;
      else if (label.includes(q)) score = 1;
      else if (entry.haystack.includes(q)) score = 2;
      if (score >= 0) scored.push({ entry, score });
    }
    scored.sort((a, b) => a.score - b.score);
    return scored.slice(0, 10).map((s) => s.entry);
  }, [query, searchIndex]);

  const goToResult = (result) => {
    setActiveSection(result.sectionId);
    scrollToSection(result.sectionId);
  };

  const copyCurl = () => {
    navigator.clipboard.writeText(CURL_STREAM);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <div className="min-h-screen flex flex-col bg-slate-50 text-slate-900 dark:bg-slate-950 dark:text-slate-100 antialiased transition-colors">
      {/* Ambient background */}
      <div className="pointer-events-none fixed inset-0 overflow-hidden" aria-hidden="true">
        <div className="absolute -top-40 left-1/2 -translate-x-1/2 w-[52rem] h-[30rem] rounded-full bg-gradient-to-br from-indigo-200/40 via-purple-200/30 to-transparent dark:from-indigo-800/15 dark:via-purple-800/10 blur-3xl" />
      </div>

      {/* Pill header */}
      <header className="sticky top-4 z-50 mx-4 sm:mx-6">
        <div
          className={`max-w-6xl mx-auto flex items-center justify-between pl-3 pr-3 sm:pl-4 sm:pr-3 py-2.5 rounded-full bg-white/70 dark:bg-slate-900/70 backdrop-blur-xl border border-slate-200/80 dark:border-slate-700/80 transition-shadow ${
            elevated ? 'shadow-lg shadow-slate-900/5 dark:shadow-black/30' : 'shadow-sm'
          }`}
        >
          <Link to="/" className="flex items-center gap-2.5 font-semibold tracking-tight">
            <span className="inline-grid place-items-center w-9 h-9 rounded-full bg-gradient-to-br from-indigo-600 via-purple-600 to-indigo-700 text-white text-sm font-bold shadow-md shadow-indigo-500/25">
              QC
            </span>
            <span className="hidden sm:inline-block text-[15px]">Quality Compute</span>
          </Link>
          <nav className="hidden md:flex items-center gap-7 text-sm font-medium text-slate-600 dark:text-slate-300">
            <Link to="/" className="hover:text-slate-900 dark:hover:text-white transition-colors">Home</Link>
            <Link to="/api-reference" className="text-indigo-600 dark:text-indigo-400 font-semibold">API Reference</Link>
          </nav>
          <div className="flex items-center gap-1.5">
            <ThemeToggle />
            <Link
              to="/login"
              className="hidden sm:inline-flex px-4 py-2 rounded-full text-sm font-medium text-slate-600 hover:text-slate-900 hover:bg-slate-100 dark:text-slate-300 dark:hover:text-white dark:hover:bg-slate-800 transition-colors"
            >
              Login
            </Link>
            <Link
              to="/register"
              className="inline-flex items-center gap-2 px-4 sm:px-5 py-2 rounded-full bg-slate-900 text-white dark:bg-white dark:text-slate-900 text-sm font-medium hover:bg-slate-700 dark:hover:bg-slate-200 transition-colors shadow-sm"
            >
              Register
            </Link>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="relative flex-1 py-12 sm:py-16 px-4 sm:px-6">
        <div className="max-w-6xl mx-auto">
          {/* Page Header */}
          <div className="mb-12 max-w-3xl">
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-indigo-50 border border-indigo-100 dark:bg-indigo-950/60 dark:border-indigo-900 text-xs font-semibold text-indigo-700 dark:text-indigo-300 uppercase tracking-wider mb-4">
              Documentation
            </div>
            <h1 className="text-4xl sm:text-5xl font-extrabold tracking-tight mb-4">
              API{' '}
              <span className="bg-gradient-to-r from-indigo-600 to-purple-600 dark:from-indigo-400 dark:to-purple-400 text-transparent bg-clip-text">Reference</span>
            </h1>
            <p className="text-lg text-slate-600 dark:text-slate-400 leading-relaxed">
              Comprehensive documentation for the Quality Compute API: streaming-first generation
              with opt-in best-of-N, judge selection, synthesis, and collaborative strategies.
            </p>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-4 gap-8">
            {/* Sidebar Navigation */}
            <aside className="lg:col-span-1">
              <div className="bg-white/80 dark:bg-slate-900/80 backdrop-blur-sm rounded-2xl border border-slate-200/80 dark:border-slate-800 shadow-sm p-5 sticky top-24">
                {/* Search */}
                <div className="relative mb-4">
                  <svg
                    className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400 dark:text-slate-500 pointer-events-none"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    viewBox="0 0 24 24"
                  >
                    <path strokeLinecap="round" strokeLinejoin="round" d="M21 21l-4.35-4.35M17 10.5a6.5 6.5 0 11-13 0 6.5 6.5 0 0113 0z" />
                  </svg>
                  <input
                    type="search"
                    value={query}
                    onChange={(e) => setQuery(e.target.value)}
                    placeholder="Search the docs…"
                    aria-label="Search the API reference"
                    className="w-full pl-9 pr-3 py-2 rounded-xl text-sm bg-slate-100/80 dark:bg-slate-800/80 border border-transparent focus:border-indigo-300 dark:focus:border-indigo-700 focus:bg-white dark:focus:bg-slate-900 focus:outline-none focus:ring-2 focus:ring-indigo-500/20 placeholder:text-slate-400 dark:placeholder:text-slate-500 transition-colors"
                  />
                </div>

                {query.trim().length >= 2 ? (
                  /* Search results */
                  <div>
                    <h3 className="text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wider mb-3 px-1">
                      {searchResults.length === 0 ? 'No matches' : `Results (${searchResults.length})`}
                    </h3>
                    <div className="space-y-1 max-h-[26rem] overflow-y-auto pr-1">
                      {searchResults.map((result) => (
                        <button
                          key={`${result.kind}-${result.label}`}
                          type="button"
                          onClick={() => goToResult(result)}
                          className="w-full text-left px-3 py-2 rounded-lg hover:bg-slate-50 dark:hover:bg-slate-800 transition-colors"
                        >
                          <div className="flex items-center gap-2 mb-0.5">
                            <span className={`text-[10px] font-semibold px-1.5 py-0.5 rounded ${SEARCH_KIND_STYLES[result.kind] || SEARCH_KIND_STYLES.Section}`}>
                              {result.kind}
                            </span>
                            <span className="text-sm font-medium font-mono truncate">{result.label}</span>
                          </div>
                          <div className="text-xs text-slate-500 dark:text-slate-400 truncate">
                            {SECTION_LABELS[result.sectionId]} · {result.snippet}
                          </div>
                        </button>
                      ))}
                      {searchResults.length === 0 && (
                        <p className="px-3 py-2 text-sm text-slate-500 dark:text-slate-400">
                          Nothing found for “{query.trim()}”.
                        </p>
                      )}
                    </div>
                  </div>
                ) : (
                  /* Section nav */
                  <div>
                    <h3 className="text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wider mb-3 px-3">
                      On this page
                    </h3>
                    <nav className="space-y-0.5">
                      {SECTIONS.map((section) => {
                        const active = activeSection === section.id;
                        return (
                          <a
                            key={section.id}
                            href={`#${section.id}`}
                            onClick={(e) => {
                              e.preventDefault();
                              setActiveSection(section.id);
                              scrollToSection(section.id);
                            }}
                            className={`relative block px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                              active
                                ? 'bg-indigo-50 text-indigo-700 dark:bg-indigo-950/60 dark:text-indigo-300'
                                : 'text-slate-600 hover:bg-slate-50 hover:text-slate-900 dark:text-slate-400 dark:hover:bg-slate-800 dark:hover:text-slate-100'
                            }`}
                          >
                            {active && (
                              <span className="absolute left-0 top-1/2 -translate-y-1/2 w-1 h-4 rounded-full bg-gradient-to-b from-indigo-500 to-purple-500" aria-hidden="true" />
                            )}
                            {section.label}
                          </a>
                        );
                      })}
                    </nav>
                  </div>
                )}

                <div className="mt-5 pt-5 border-t border-slate-100 dark:border-slate-800">
                  <Link
                    to="/register"
                    className="inline-flex items-center justify-center w-full px-4 py-2.5 rounded-xl bg-gradient-to-r from-indigo-600 to-purple-600 text-white text-sm font-semibold hover:from-indigo-500 hover:to-purple-500 transition-colors shadow-md shadow-indigo-500/20"
                  >
                    Get an API key
                  </Link>
                </div>
              </div>
            </aside>

            {/* Main Documentation Content */}
            <div className="lg:col-span-3 space-y-8">
              {/* Introduction */}
              <SectionCard id="introduction" title="Introduction">
                <p className="text-slate-600 dark:text-slate-400 mb-6 leading-relaxed">
                  The Quality Compute API is a streaming-first gateway to frontier language models across seven providers.
                  By default a request makes a single model call; you can opt into quality-boosting strategies — best-of-N
                  selection, judge selection, synthesis, and collaborative multi-model sessions — either with explicit
                  strategy fields or with the {inlineCode('-B[N]')} model-suffix shorthand.
                  The supported model list on this page is rendered from the bundled catalog snapshot so the documentation remains available even when the backend is unreachable from the browser.
                </p>
                <h3 className="text-lg font-semibold mb-3">Base URL</h3>
                <div className="bg-slate-900 rounded-xl p-4 border border-slate-800 dark:border-slate-700/70 font-mono text-sm text-slate-200 mb-6">
                  https://qualitycompute.henosis.us
                </div>
                <h3 className="text-lg font-semibold mb-3">Quick Example — Streaming (SSE)</h3>
                <div className="rounded-xl overflow-hidden border border-slate-800 dark:border-slate-700/70 mb-4 shadow-sm">
                  <div className="flex items-center justify-between px-4 py-2.5 bg-slate-800 border-b border-slate-700/70">
                    <div className="flex items-center gap-3">
                      <div className="flex gap-1.5">
                        <span className="w-3 h-3 rounded-full bg-red-400/90" />
                        <span className="w-3 h-3 rounded-full bg-yellow-400/90" />
                        <span className="w-3 h-3 rounded-full bg-green-400/90" />
                      </div>
                      <span className="text-xs font-medium text-slate-400 font-mono">cURL</span>
                    </div>
                    <button
                      onClick={copyCurl}
                      className="text-xs font-medium text-slate-300 bg-slate-700/60 border border-slate-600/60 px-3 py-1 rounded-md hover:bg-slate-600/60 transition-colors"
                    >
                      {copied ? 'Copied ✓' : 'Copy'}
                    </button>
                  </div>
                  <pre className="p-4 sm:p-5 overflow-x-auto text-[13px] leading-relaxed font-mono text-slate-300 bg-slate-900">
                    <code className="block whitespace-pre">{CURL_STREAM}</code>
                  </pre>
                </div>
                <p className="text-slate-600 dark:text-slate-400 mb-4 leading-relaxed">
                  The {inlineCode('-B10')} suffix on {inlineCode('"o4-mini-B10"')} runs
                  best-of-10: ten drafts are generated in parallel, a judge picks the best one, and the winner streams back.
                  Three things are mandatory on every generation call: the {inlineCode('Authorization')} header,
                  an {inlineCode('Idempotency-Key')} header, and {inlineCode('"stream": true')} in
                  the body. The response is a Server-Sent Events stream (truncated):
                </p>
                {codeBlock(SSE_SAMPLE)}
                <h3 className="text-lg font-semibold mb-3">Quick Example — Buffered JSON</h3>
                <p className="text-slate-600 dark:text-slate-400 mb-4 leading-relaxed">
                  If you'd rather get a single JSON object back, use {inlineCode('POST /v1/responses')}.
                  The server runs the same streaming pipeline internally (so {inlineCode('"stream": true')} is
                  still required in the body) and returns the final {inlineCode('response.completed')} summary.
                  This example spells out best-of-N with explicit strategy fields instead of the suffix:
                </p>
                {codeBlock(CURL_BUFFERED)}
                <p className="text-slate-600 dark:text-slate-400 leading-relaxed">
                  {inlineCode('POST /api/generate')} still works as a legacy alias of{' '}
                  {inlineCode('/v1/responses/stream')}; new integrations should use the{' '}
                  {inlineCode('/v1')} endpoints.
                </p>
              </SectionCard>

              {/* Authentication */}
              <SectionCard id="authentication" title="Authentication">
                <p className="text-slate-600 dark:text-slate-400 mb-6 leading-relaxed">
                  All API requests authenticate with an API key sent as a Bearer token. Keys look like{' '}
                  {inlineCode('QC_')} followed by 32 hex characters, and are minted in the user
                  dashboard after registration and verification. Only the {inlineCode('Authorization')}{' '}
                  header is accepted — {inlineCode('x-api-key')} is not supported.
                </p>
                <div className="bg-slate-900 rounded-xl p-4 border border-slate-800 dark:border-slate-700/70 font-mono text-sm text-slate-200 mb-6 overflow-x-auto">
                  Authorization: Bearer QC_0123456789abcdef0123456789abcdef
                </div>
                <h3 className="text-lg font-semibold mb-3">What an API key can do</h3>
                <p className="text-slate-600 dark:text-slate-400 mb-4">
                  API keys cover generation, estimation, runs, metadata, and read-only account self-service:
                </p>
                <ul className="list-disc pl-6 text-slate-600 dark:text-slate-400 space-y-2 mb-6 text-sm">
                  <li>{inlineCode('GET /v1/usage')} — your usage ledger (JSON, or CSV with {inlineCode('?format=csv')}).</li>
                  <li>{inlineCode('GET /v1/credits')} — remaining credit balance and payment history.</li>
                  <li>{inlineCode('GET /v1/me')} — the key's owner, label, scopes, and budget caps (the raw key is never echoed back).</li>
                </ul>
                {infoBox(
                  <p>
                    <strong>Dashboard vs. API key:</strong> key management (creating, rotating, and revoking keys), budget-cap
                    configuration, and subscription management are dashboard-only — they require a dashboard session token,
                    not an API key. Everything else documented on this page works with your API key.
                  </p>
                )}
                {infoBox(
                  <p>
                    Keep your API key secure and don't share it publicly. If a key leaks, rotate it from the dashboard.
                  </p>
                )}
              </SectionCard>

              {/* Endpoints */}
              <SectionCard id="endpoints" title="Endpoints">
                <p className="text-slate-600 dark:text-slate-400 mb-6 leading-relaxed">
                  {inlineCode('/v1/*')} paths are canonical; {inlineCode('/api/*')} paths
                  are legacy aliases kept for backward compatibility. All generation endpoints require the{' '}
                  {inlineCode('Authorization')} and {inlineCode('Idempotency-Key')} headers
                  and a body with {inlineCode('"stream": true')}.
                </p>

                <h3 className="text-lg font-semibold mb-3">Generation</h3>
                {renderEndpointTable(GENERATION_ENDPOINTS)}
                {codeBlock(WS_SAMPLE)}

                <h3 className="text-lg font-semibold mb-3">Strategy shortcuts</h3>
                <p className="text-slate-600 dark:text-slate-400 mb-4 text-sm">
                  These are equivalent to {inlineCode('/v1/responses/stream')} with the corresponding{' '}
                  {inlineCode('strategy')} forced in the body:
                </p>
                {renderEndpointTable(STRATEGY_ENDPOINTS)}

                <h3 className="text-lg font-semibold mb-3">Cost & token estimation</h3>
                {renderEndpointTable(ESTIMATION_ENDPOINTS)}

                <h3 className="text-lg font-semibold mb-3">Account self-service (API key)</h3>
                {renderEndpointTable(ACCOUNT_ENDPOINTS)}

                <h3 className="text-lg font-semibold mb-3">Durable runs</h3>
                <p className="text-slate-600 dark:text-slate-400 mb-4 text-sm">
                  Runs persist the request and its event log server-side, so long jobs survive client disconnects and can be
                  resumed or replayed:
                </p>
                {renderEndpointTable(RUNS_ENDPOINTS)}

                <h3 className="text-lg font-semibold mb-3">Metadata & health</h3>
                {renderEndpointTable(METADATA_ENDPOINTS)}

                <h3 className="text-lg font-semibold mb-3">Legacy aliases & tools</h3>
                {renderEndpointTable(LEGACY_ENDPOINTS)}
              </SectionCard>

              {/* Request Parameters */}
              <SectionCard id="parameters" title="Request Parameters">
                <p className="text-slate-600 dark:text-slate-400 mb-6 leading-relaxed">
                  All generation endpoints share one request schema. Core parameters:
                </p>
                {renderParamTable(CORE_PARAMS)}
                {infoBox(
                  <>
                    <p>
                      <strong>The -B[N] suffix:</strong> appending {inlineCode('-B[N]')} to the model id
                      (e.g. {inlineCode('"o4-mini-B10"')}) is shorthand for best-of-N. When the body's{' '}
                      {inlineCode('strategy')} is left at the default {inlineCode('single')},
                      a {inlineCode('-BN')} suffix with N from 2 to 32 becomes{' '}
                      {inlineCode('strategy: "best_of_n"')} with {inlineCode('candidates: N')} in
                      selection mode. A bare {inlineCode('-B')} means 8 candidates; {inlineCode('-B1')} stays
                      a single call. N outside 1–32 is rejected with a 400. The suffix is always stripped before model validation
                      and billing (it also works on {inlineCode('judge_model')}), and explicit{' '}
                      {inlineCode('strategy')}/{inlineCode('candidates')} fields in the body
                      always win over the suffix.
                    </p>
                    <p>
                      <strong>Removed parameters:</strong> {inlineCode('temperature')},{' '}
                      {inlineCode('top_p')}, {inlineCode('top_k')},{' '}
                      {inlineCode('presence_penalty')}, {inlineCode('frequency_penalty')},{' '}
                      {inlineCode('include_candidates')}, and {inlineCode('max_tokens')} do
                      not exist in this API. Use {inlineCode('max_output_tokens')} for length control.
                    </p>
                  </>
                )}
                <h3 className="text-lg font-semibold mb-3">Strategy parameters</h3>
                {renderParamTable(STRATEGY_PARAMS)}
                <h3 className="text-lg font-semibold mb-3">Advanced: tools</h3>
                <p className="text-slate-600 dark:text-slate-400 mb-4 text-sm leading-relaxed">
                  Pass your own function tools with {inlineCode('user_tools')} (a list of{' '}
                  {inlineCode('{"type": "function", "name", "description", "parameters"}')} objects,
                  where {inlineCode('parameters')} is a JSON Schema). When the model calls a tool you'll
                  receive {inlineCode('response.tool_call')} events, and the final summary sets{' '}
                  {inlineCode('awaiting_tool_results: true')}. Execute the tool yourself, then continue the
                  conversation by sending {inlineCode('tool_results')} (a list of{' '}
                  {inlineCode('{"name", "output", "tool_call_id"}')}) in a follow-up request, or
                  acknowledge out-of-band executions via {inlineCode('POST /v1/tools/callback')}. Related
                  knobs: {inlineCode('tool_mode')} (AUTO | ANY | NONE); Anthropic{' '}
                  {inlineCode('claude-opus-5-5')} accepts only AUTO/NONE and rejects legacy ANY,{' '}
                  {inlineCode('parallel_tool_calls')}, and {inlineCode('include_builtin_tools')}.
                </p>
                <h3 className="text-lg font-semibold mb-3">Advanced: web search</h3>
                <p className="text-slate-600 dark:text-slate-400 text-sm leading-relaxed">
                  Set {inlineCode('enable_web_search: true')} to allow provider-native web search on models
                  that support it. Filter and shape results with {inlineCode('web_search_allowed_domains')}{' '}
                  (a list of domains), {inlineCode('web_search_include_sources')} (include source citations),
                  and {inlineCode('web_search_user_location')}{' '}
                  ({inlineCode('{"type": "approximate", "country", "city", "region", "timezone"}')}).
                  Search activity is reported in the {inlineCode('web_search')} array of the final summary.
                </p>
              </SectionCard>

              {/* Streaming & Responses */}
              <SectionCard id="streaming" title="Streaming & Responses">
                <p className="text-slate-600 dark:text-slate-400 mb-6 leading-relaxed">
                  Streaming endpoints return {inlineCode('text/event-stream')}. Each SSE frame has an{' '}
                  {inlineCode('event:')} line naming the type and a {inlineCode('data:')}{' '}
                  line with a JSON payload that repeats the type in a {inlineCode('type')} field. Event types:
                </p>
                <div className="rounded-xl border border-slate-200 dark:border-slate-800 overflow-hidden mb-6">
                  <div className="overflow-x-auto">
                    <table className="min-w-full divide-y divide-slate-200 dark:divide-slate-800">
                      <thead className="bg-slate-50/80 dark:bg-slate-800/60">
                        <tr>
                          {tableHeadCell('Event')}
                          {tableHeadCell('Meaning')}
                        </tr>
                      </thead>
                      <tbody className="bg-white dark:bg-slate-900 divide-y divide-slate-100 dark:divide-slate-800/70 text-sm">
                        {EVENT_TYPES.map((event) => (
                          <tr key={event.name} className="hover:bg-slate-50/60 dark:hover:bg-slate-800/40 transition-colors">
                            <td className="px-4 py-3 font-mono text-[13px] text-indigo-700 dark:text-indigo-300 whitespace-nowrap">{event.name}</td>
                            <td className="px-4 py-3 text-slate-600 dark:text-slate-400">{event.desc}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
                {infoBox(
                  <p>
                    Streams also interleave legacy alias events ({inlineCode('session.started')},{' '}
                    {inlineCode('message.delta')}, {inlineCode('tool.call')},{' '}
                    {inlineCode('message.completed')}) for older clients. New integrations should key on
                    the {inlineCode('response.*')} types and ignore the rest. Anthropic{' '}
                    {inlineCode('claude-opus-5-5')} emits {inlineCode('response.thinking.delta')}{' '}
                    progress text by default with insights off; the event contains no signature. Use{' '}
                    {inlineCode('anthropic_thinking_display: "summarized"')} or explicit thinking insights
                    when a summarized view is wanted.
                  </p>
                )}
                <h3 className="text-lg font-semibold mb-3">The response.completed summary</h3>
                <p className="text-slate-600 dark:text-slate-400 mb-4 leading-relaxed">
                  The terminal event carries the full execution summary. The buffered{' '}
                  {inlineCode('POST /v1/responses')} endpoint returns this same object as its JSON body:
                </p>
                {codeBlock(COMPLETED_EVENT_SAMPLE)}
                <p className="text-slate-600 dark:text-slate-400 text-sm leading-relaxed">
                  {inlineCode('usage')} aggregates tokens across every internal call (all drafts plus the
                  judge for best-of-N), and {inlineCode('bill')} is the amount charged to your credits.
                  Additional fields appear when relevant: {inlineCode('tool_calls')},{' '}
                  {inlineCode('web_search')}, {inlineCode('service_tier')},{' '}
                  {inlineCode('refusal')}, {inlineCode('stop_details')}, and{' '}
                  {inlineCode('awaiting_tool_results')}.
                </p>
              </SectionCard>

              {/* Idempotency */}
              <SectionCard id="idempotency" title="Idempotency">
                <p className="text-slate-600 dark:text-slate-400 mb-6 leading-relaxed">
                  Every generation endpoint requires an {inlineCode('Idempotency-Key')} header — any unique
                  string you choose (a UUID works well). Omitting it returns{' '}
                  {inlineCode('400 {"detail": "Idempotency-Key header is required"}')}.
                </p>
                <div className="bg-slate-900 rounded-xl p-4 border border-slate-800 dark:border-slate-700/70 font-mono text-sm text-slate-200 mb-6 overflow-x-auto">
                  Idempotency-Key: 6f1c2c9e-8a4b-4b3f-9d2e-example
                </div>
                <ul className="list-disc pl-6 text-slate-600 dark:text-slate-400 space-y-2 text-sm mb-4">
                  <li><strong>Replay:</strong> re-sending the same key with the same payload replays the stored result (the full event stream on streaming endpoints, the completed summary on buffered ones) without re-running or re-billing the request.</li>
                  <li><strong>Conflict — different payload:</strong> the same key with a different payload returns {inlineCode('409 Conflict')}.</li>
                  <li><strong>Conflict — in progress:</strong> if a request with the same key is still running, you get {inlineCode('409 Conflict')} until it finishes.</li>
                  <li><strong>TTL:</strong> stored results expire after 24 hours; after that the key can be reused as new.</li>
                </ul>
                {infoBox(
                  <p>
                    Use one fresh key per logical request and retry with the <em>same</em> key on network failures — that gives
                    you exactly-once semantics for both output and billing.
                  </p>
                )}
              </SectionCard>

              {/* Billing & Budgets */}
              <SectionCard id="billing" title="Billing & Budgets">
                <p className="text-slate-600 dark:text-slate-400 mb-6 leading-relaxed">
                  Every generation call is cost-estimated <em>before</em> it runs. The estimate covers all internal calls —
                  for best-of-N that's all N drafts plus one judge call (the same is true when best-of-N comes from a{' '}
                  {inlineCode('-BN')} suffix). If the estimate exceeds any applicable limit, the request is
                  rejected with {inlineCode('402 Payment Required')} and the reason in{' '}
                  {inlineCode('detail')} (e.g. "Estimated cost exceeds remaining credits", "Estimated cost
                  exceeds request_budget_usd"). Nothing is charged for denied requests.
                </p>
                <h3 className="text-lg font-semibold mb-3">Limits checked, in order</h3>
                <ul className="list-disc pl-6 text-slate-600 dark:text-slate-400 space-y-2 text-sm mb-6">
                  <li>Plan entitlements (model, strategy, service tier).</li>
                  <li>Per-request caps from the body: {inlineCode('request_budget_usd')}, {inlineCode('api_key_budget_usd')}, {inlineCode('user_budget_usd')}.</li>
                  <li>Persistent per-key and per-user budget caps configured in the dashboard.</li>
                  <li>Your remaining credit balance.</li>
                </ul>
                <h3 className="text-lg font-semibold mb-3">Checking costs without spending</h3>
                <ul className="list-disc pl-6 text-slate-600 dark:text-slate-400 space-y-2 text-sm mb-4">
                  <li>{inlineCode('POST /v1/cost-estimates')} — dry-run any request body: returns {inlineCode('estimated_usage')}, {inlineCode('estimated_bill')}, {inlineCode('allowed')}, and {inlineCode('denial_reason')} with no generation and no charge.</li>
                  <li>{inlineCode('POST /v1/cost-plans')} — the same estimate broken down per internal call.</li>
                  <li>{inlineCode('GET /v1/credits')} — your current balance and payment ledger.</li>
                  <li>{inlineCode('GET /v1/usage')} — what past requests actually cost (JSON or CSV).</li>
                </ul>
                {infoBox(
                  <p>
                    Actual billed cost is reported in the {inlineCode('bill')} object of every{' '}
                    {inlineCode('response.completed')} summary, with token detail in{' '}
                    {inlineCode('usage')}.
                  </p>
                )}
              </SectionCard>

              {/* Supported Models */}
              <SectionCard id="models" title="Supported Models">
                <p className="text-slate-600 dark:text-slate-400 mb-6 leading-relaxed">
                  Quality Compute publishes its supported models via {inlineCode('GET /v1/models')}{' '}
                  (legacy alias {inlineCode('/api/models')}). This section is rendered from a bundled snapshot of that backend
                  catalog, so the documentation page does not fail when the browser cannot reach the API. Only canonical, enabled model ids appear
                  in the list; aliases (e.g. dated snapshot names) resolve automatically when used in requests but are not
                  listed separately. Requesting a model that isn't in the catalog returns{' '}
                  {inlineCode("400 Unknown model '<id>'")}.
                </p>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
                  {Object.entries(groupedModels).map(([provider, models]) => (
                    <div
                      key={provider}
                      className="border border-slate-200/80 dark:border-slate-800 rounded-xl p-5 bg-slate-50/40 dark:bg-slate-800/30 hover:border-indigo-200 dark:hover:border-indigo-800 hover:bg-white dark:hover:bg-slate-800/60 transition-colors"
                    >
                      <div className="flex items-center gap-3 mb-3">
                        <span
                          className={`inline-grid place-items-center w-8 h-8 rounded-lg text-white text-sm font-bold ${
                            PROVIDER_STYLES[provider] || 'bg-slate-400'
                          }`}
                        >
                          {provider.charAt(0).toUpperCase()}
                        </span>
                        <h3 className="text-lg font-semibold capitalize">{provider}</h3>
                      </div>
                      <ul className="space-y-1.5">
                        {models.map((model) => (
                          <li key={model.id} className="flex items-center flex-wrap gap-x-2 gap-y-1">
                            <span className="font-mono text-[13px] text-slate-700 dark:text-slate-300">{model.id}</span>
                            {model.capabilities?.thinking && (
                              <span className="text-[10px] font-semibold uppercase tracking-wider px-1.5 py-0.5 rounded-md bg-indigo-50 text-indigo-600 ring-1 ring-inset ring-indigo-600/10 dark:bg-indigo-950/60 dark:text-indigo-300 dark:ring-indigo-400/20">
                                thinking
                              </span>
                            )}
                            {model.capabilities?.reasoning && (
                              <span className="text-[10px] font-semibold uppercase tracking-wider px-1.5 py-0.5 rounded-md bg-purple-50 text-purple-600 ring-1 ring-inset ring-purple-600/10 dark:bg-purple-950/60 dark:text-purple-300 dark:ring-purple-400/20">
                                reasoning
                              </span>
                            )}
                            {Array.isArray(model.speed_modes) && model.speed_modes.includes('fast') && (
                              <span className="text-[10px] font-semibold uppercase tracking-wider px-1.5 py-0.5 rounded-md bg-amber-50 text-amber-700 ring-1 ring-inset ring-amber-600/10 dark:bg-amber-950/60 dark:text-amber-300 dark:ring-amber-400/20">
                                fast
                              </span>
                            )}
                          </li>
                        ))}
                      </ul>
                    </div>
                  ))}
                </div>
                <h3 className="text-lg font-semibold mb-3">/v1/models response shape</h3>
                <p className="text-slate-600 dark:text-slate-400 mb-4 text-sm leading-relaxed">
                  Each entry includes public pricing (USD per million tokens), the context limit, aliases,{' '}
                  {inlineCode('speed_modes')} / {inlineCode('default_speed_mode')}, and capability flags
                  ({inlineCode('best_of_n')}, {inlineCode('selection')},{' '}
                  {inlineCode('synthesis')}, {inlineCode('native_web_search')},{' '}
                  {inlineCode('thinking')}, {inlineCode('reasoning')},{' '}
                  {inlineCode('streaming')}, …). Request Anthropic fast mode via{' '}
                  {inlineCode('anthropic_speed: "fast"')} when the model lists it. Models whose provider key
                  isn&apos;t configured are omitted:
                </p>
                {codeBlock(MODELS_RESPONSE_SAMPLE)}
                {infoBox(
                  <p>
                    To use best-of-N inference, append {inlineCode('-B[N]')} to any model id, where N is the
                    number of candidates from 2 to 32 (e.g. {inlineCode('"o4-mini-B5"')}). A bare{' '}
                    {inlineCode('-B')} defaults to 8 candidates; {inlineCode('-B1')} or no
                    suffix means a single call. Prefer explicit {inlineCode('strategy')} +{' '}
                    {inlineCode('candidates')} fields for anything beyond quick experiments — they always
                    take precedence over the suffix.
                  </p>
                )}
              </SectionCard>

              {/* Error Handling */}
              <SectionCard id="error-handling" title="Error Handling">
                <p className="text-slate-600 dark:text-slate-400 mb-6 leading-relaxed">
                  The API uses conventional HTTP status codes for failures that occur before a stream starts. Once a stream
                  has started, failures arrive as in-band {inlineCode('response.error')} events under
                  HTTP 200 — the status code never changes mid-stream. There is currently no rate limiting on the API.
                </p>
                <div className="rounded-xl border border-slate-200 dark:border-slate-800 overflow-hidden mb-6">
                  <div className="overflow-x-auto">
                    <table className="min-w-full divide-y divide-slate-200 dark:divide-slate-800">
                      <thead className="bg-slate-50/80 dark:bg-slate-800/60">
                        <tr>
                          {tableHeadCell('Code')}
                          {tableHeadCell('Status')}
                          {tableHeadCell('Description')}
                        </tr>
                      </thead>
                      <tbody className="bg-white dark:bg-slate-900 divide-y divide-slate-100 dark:divide-slate-800/70 text-sm">
                        {STATUS_CODES.map((row) => (
                          <tr key={row.code} className="hover:bg-slate-50/60 dark:hover:bg-slate-800/40 transition-colors">
                            <td className="px-4 py-3 font-mono text-[13px] text-slate-700 dark:text-slate-300">{row.code}</td>
                            <td className={`px-4 py-3 font-semibold whitespace-nowrap ${row.tone}`}>{row.status}</td>
                            <td className="px-4 py-3 text-slate-600 dark:text-slate-400">{row.desc}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
                <h3 className="text-lg font-semibold mb-3">Error Response Format</h3>
                {codeBlock(ERROR_DETAIL_SAMPLE)}
                <h3 className="text-lg font-semibold mb-3">Mid-stream errors</h3>
                {codeBlock(STREAM_ERROR_SAMPLE)}
                <p className="text-slate-600 dark:text-slate-400 text-sm leading-relaxed">
                  On the WebSocket endpoint, budget denials are sent as a {inlineCode('response.error')}{' '}
                  message followed by close code 1008; other errors close with 1011.
                </p>
              </SectionCard>
            </div>
          </div>
        </div>
      </main>

      {/* Footer */}
      <footer className="relative py-10 border-t border-slate-200 dark:border-slate-800 bg-white/60 dark:bg-slate-900/60 backdrop-blur-sm px-4 sm:px-6 mt-12">
        <div className="max-w-6xl mx-auto flex flex-col sm:flex-row justify-between items-center gap-4 text-sm text-slate-500 dark:text-slate-400">
          <div className="flex items-center gap-2.5">
            <span className="inline-grid place-items-center w-7 h-7 rounded-full bg-gradient-to-br from-indigo-600 via-purple-600 to-indigo-700 text-white text-[10px] font-bold">
              QC
            </span>
            <span>© {new Date().getFullYear()} Quality Compute</span>
          </div>
          <div className="flex flex-wrap justify-center gap-6">
            <Link to="/privacy" className="hover:text-indigo-600 dark:hover:text-indigo-400 transition-colors">Privacy Policy</Link>
            <Link to="/terms" className="hover:text-indigo-600 dark:hover:text-indigo-400 transition-colors">Terms of Service</Link>
            <Link to="/contact" className="hover:text-indigo-600 dark:hover:text-indigo-400 transition-colors">Contact</Link>
          </div>
        </div>
      </footer>
    </div>
  );
}
